import React, { useState, useEffect } from "react";
import { Sparkles, Zap, ShieldCheck, HelpCircle, Lightbulb } from "lucide-react";

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      src: "/img/electronics-kit/hero_slide_desk_fan.jpg",
      alt: "DIY Desk Fan electronics kit project with DC motor, battery, and paper cup",
      caption: "Project 6 • DIY Desk Fan 🌪️",
      fitClass: "cover-fit",
    },
    {
      src: "/img/electronics-kit/p1_rocket_card_try_it.png",
      alt: "Rocket Card circuit glowing with LED and battery",
      caption: "Project 1 • Glowing Rocket Card 🚀",
      fitClass: "contain-fit",
    },
    {
      src: "/img/electronics-kit/hero_slide_traffic_light.jpg",
      alt: "DIY Traffic Light STEM circuit with glowing Red, Yellow, Green LEDs",
      caption: "Project 5 • Traffic Signal Station 🚦",
      fitClass: "cover-fit",
    },
    {
      src: "/img/electronics-kit/p2_my_first_light_try_it.png",
      alt: "My First Light circuit with 9V battery and LED",
      caption: "Project 2 • My First Light Circuit 💡",
      fitClass: "contain-fit",
    },
    {
      src: "/img/electronics-kit/rule_3_how_to_join.png",
      alt: "Solderless Twist & Tape joint instructions",
      caption: "Golden Rule • Solderless Twist & Tape 🛠️",
      fitClass: "contain-fit",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="section kit-hero-section" id="hero">
      <div className="container">
        <div className="hero-grid">
          {/* Left Column: Hero Content */}
          <div className="hero-content reveal-triggered fade-left active" data-aos="fade-right" data-aos-duration="1000">
            <div className="badge hero-badge" data-aos="zoom-in" data-aos-delay="100">
              <Sparkles size={16} className="text-amber-500" /> 🚀 Junior Inventors Lab • Official Kit Manual
            </div>

            <h1 className="hero-title" data-aos="fade-right" data-aos-delay="200">
              Build 15 Fun Electronics Projects <br />
              <span>With Simple Things From Home</span>
            </h1>

            <div className="hero-supporting-line" data-aos="fade-right" data-aos-delay="300">
              ✨ Discover • Build • Test • Play ✨
            </div>

            <p className="hero-description" data-aos="fade-right" data-aos-delay="350">
              Welcome young inventor! Your Practido Fun Kit comes packed with exciting electronic parts—battery, LEDs,
              buzzers, motors, and buttons. Together with everyday items like kitchen foil, paper cups, and cardboard, you
              will construct 15 amazing real-world circuits from scratch!
            </p>

            {/* Quick Action Buttons */}
            <div className="hero-buttons" data-aos="zoom-in" data-aos-delay="400">
              <a href="#projects" className="btn kit-browse-btn">
                <Zap size={16} /> 🚀 Browse 15 Projects
              </a>
              <a href="#safety-basics" className="btn outline-btn">
                <ShieldCheck size={16} /> 🛡️ 3 Golden Rules
              </a>
              <a href="#kit-contents" className="btn secondary-btn">
                📦 What's in Your Kit?
              </a>
            </div>

            {/* Stat Cards Row */}
            <div className="hero-stats-row" data-aos="fade-up" data-aos-delay="450">
              <div className="stat-card">
                <div className="stat-icon stat-blue">
                  <Zap size={18} strokeWidth={2.5} />
                </div>
                <div className="stat-number">15</div>
                <div className="stat-label">Fun Projects</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon stat-green">
                  <ShieldCheck size={18} strokeWidth={2.5} />
                </div>
                <div className="stat-number">0</div>
                <div className="stat-label">Soldering Needed</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon stat-orange">
                  <Sparkles size={18} strokeWidth={2.5} />
                </div>
                <div className="stat-number">9</div>
                <div className="stat-label">Core Kit Parts</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon stat-purple">
                  <HelpCircle size={18} strokeWidth={2.5} />
                </div>
                <div className="stat-number">24/7</div>
                <div className="stat-label">WhatsApp Help</div>
              </div>
            </div>
          </div>

          {/* Right Column: Hero Media Showcase */}
          <div className="hero-media reveal-triggered fade-right delay-200 active" data-aos="fade-left" data-aos-duration="1000">
            <div className="floating-top-badge" data-aos="zoom-in" data-aos-delay="300">
              <Lightbulb size={16} />
              <span>15 Real-World Circuits</span>
            </div>

            <div className="hero-img-wrap">
              <div
                className="hero-carousel-track"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {slides.map((slide, index) => (
                  <div key={index} className="hero-carousel-slide">
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      className={slide.fitClass || ""}
                    />
                    {slide.caption && (
                      <div className="slide-caption-badge">
                        <span>{slide.caption}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="carousel-dots">
                {slides.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${currentSlide === index ? "active" : ""}`}
                    onClick={() => setCurrentSlide(index)}
                  ></span>
                ))}
              </div>
            </div>

            {/* Floating Overlay Badge Card */}
            <div className="floating-overlay-card zoom-in active" data-aos="zoom-in" data-aos-delay="500">
              <div className="floating-overlay-icon">
                <ShieldCheck size={22} strokeWidth={2.5} />
              </div>
              <div className="floating-overlay-text">
                <h4>100% Solder-Free</h4>
                <p>Safe Twist & Tape Joints</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
